/**
 * The citation chips and the citation picker (wave b12 frontend item 8; wave STD ruling
 * 12), the same on the five registers that cite the library: ITP items, verification
 * types, decision rules, checklist bank lines and QC programme rows.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * A chip names the standard, the edition and the clause, with the clause's title as its
 * hover text; a citation of a superseded edition carries the "Superseded edition" mark
 * (the edition change moves it across). With `mayEdit` (the configuration capability the
 * server answers; citing is a CONFIGURE_RULES act) each chip has its remove control and
 * "Cite a clause" opens the picker: standard, edition, then the clause from its tree. A
 * refusal (409 citation.contract_state on an ended contract's plan item,
 * citation.already_cited, standards.edition_withdrawn) is said in the server's words, the
 * picker's inside the dialog, a removal's under the chips.
 */

import { X } from "lucide-react";
import { useState } from "react";

import { ApiError } from "../api/client";
import {
  cite,
  clauseTree,
  listClauses,
  listEditions,
  listStandards,
  uncite,
  type Citation,
  type CitationTarget,
} from "../api/standards";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog } from "../nav/ActionDialog";
import { RefusalBox } from "../screens/parts";
import { useQuery } from "../screens/useQuery";

function chipText(one: Citation): string {
  return strings.standards.chip(one.standardCode, one.editionLabel, one.clauseId);
}

export function CitationChips({
  citations,
  target,
  recordLabel,
  mayEdit,
  onChanged,
  testId = "citations",
}: {
  readonly citations: readonly Citation[];
  readonly target: CitationTarget;
  /** The record in words, for the picker's title ("item 4.1"). */
  readonly recordLabel: string;
  /** The caller may cite and remove (the configuration capability). */
  readonly mayEdit: boolean;
  /** A citation was added or removed: the register re-reads. */
  readonly onChanged: (message: string) => void;
  readonly testId?: string;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.standards;
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  async function remove(one: Citation): Promise<void> {
    setBusy(one.citationId);
    setError(null);
    try {
      await uncite(token, one.citationId);
      onChanged(copy.removed);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="q-ui flex flex-col gap-1" data-testid={testId}>
      <ul className="flex flex-wrap items-center gap-1" aria-label={copy.citationsLabel}>
        {citations.length === 0 && !mayEdit && (
          <li className="text-xs text-muted-foreground">{copy.noCitations}</li>
        )}
        {citations.map((one) => (
          <li
            key={one.citationId}
            className="inline-flex items-center gap-1"
            data-testid="citation-chip"
            data-superseded={one.superseded ? "true" : "false"}
          >
            <span className="q-chip" title={one.title}>
              {chipText(one)}
            </span>
            {one.superseded && (
              <span
                className="q-chip q-chip--attention"
                title={copy.supersededTitle}
                data-testid="citation-superseded"
              >
                {copy.superseded}
              </span>
            )}
            {mayEdit && (
              <button
                type="button"
                className="q-button q-button--quiet"
                aria-label={copy.remove(chipText(one))}
                data-testid="citation-remove"
                disabled={busy !== null}
                onClick={() => {
                  void remove(one);
                }}
              >
                <X aria-hidden="true" className="size-3.5" />
              </button>
            )}
          </li>
        ))}
        {mayEdit && (
          <li>
            <button
              type="button"
              className="q-button q-button--quiet"
              data-testid="citation-add"
              onClick={() => {
                setError(null);
                setPicking(true);
              }}
            >
              {copy.cite}
            </button>
          </li>
        )}
      </ul>
      {error !== null && (
        <div data-testid="citation-refusal">
          <RefusalBox error={error} />
        </div>
      )}
      {picking && (
        <CitationPickerDialog
          target={target}
          recordLabel={recordLabel}
          onClose={() => {
            setPicking(false);
          }}
          onCited={(chip) => {
            setPicking(false);
            onChanged(copy.cited(chip));
          }}
        />
      )}
    </div>
  );
}

/** Standard, then edition, then the clause from its tree; the refusal stays inside. */
export function CitationPickerDialog({
  target,
  recordLabel,
  onClose,
  onCited,
}: {
  readonly target: CitationTarget;
  readonly recordLabel: string;
  readonly onClose: () => void;
  readonly onCited: (chip: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.standards;
  const [standardId, setStandardId] = useState("");
  const [editionId, setEditionId] = useState("");
  const [clauseId, setClauseId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const standards = useQuery((signal) => listStandards(token, signal), [token]);
  const editions = useQuery(
    (signal) => (standardId === "" ? Promise.resolve([]) : listEditions(token, standardId, signal)),
    [token, standardId],
  );
  const clauses = useQuery(
    (signal) =>
      standardId === "" || editionId === ""
        ? Promise.resolve([])
        : listClauses(token, standardId, editionId, signal),
    [token, standardId, editionId],
  );
  const tree = clauses.kind === "ready" ? clauseTree(clauses.data) : [];

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const created = await cite(token, clauseId, target);
      onCited(chipText(created));
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={copy.citeHeading(recordLabel)} onClose={onClose} error={error}>
      <div className="q-field">
        <label className="q-field__label" htmlFor="citation-standard">
          {copy.citeStandard}
        </label>
        <select
          id="citation-standard"
          className="q-input"
          data-testid="citation-standard"
          value={standardId}
          onChange={(event) => {
            setStandardId(event.target.value);
            setEditionId("");
            setClauseId("");
          }}
        >
          <option value="">{copy.citeStandardChoose}</option>
          {(standards.kind === "ready" ? standards.data : []).map((one) => (
            <option key={one.id} value={one.id}>
              {one.code} {one.title}
            </option>
          ))}
        </select>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="citation-edition">
          {copy.citeEdition}
        </label>
        <select
          id="citation-edition"
          className="q-input"
          data-testid="citation-edition"
          disabled={standardId === ""}
          value={editionId}
          onChange={(event) => {
            setEditionId(event.target.value);
            setClauseId("");
          }}
        >
          <option value="">{copy.citeEditionChoose}</option>
          {(editions.kind === "ready" ? editions.data : []).map((one) => (
            <option key={one.id} value={one.id}>
              {copy.citeEditionOption(one.editionLabel, copy.status[one.status] ?? one.status)}
            </option>
          ))}
        </select>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="citation-clause">
          {copy.citeClause}
        </label>
        <ServerField field="clause_id">
          <select
            id="citation-clause"
            className="q-input"
            data-testid="citation-clause"
            disabled={editionId === ""}
            value={clauseId}
            onChange={(event) => {
              setClauseId(event.target.value);
            }}
          >
            <option value="">{copy.citeClauseChoose}</option>
            {tree.map((node) => (
              <option key={node.clause.id} value={node.clause.id}>
                {`${"  ".repeat(node.depth)}${node.clause.clauseId} ${node.clause.title}`}
              </option>
            ))}
          </select>
        </ServerField>
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="citation-send"
          disabled={busy || clauseId === ""}
          onClick={() => {
            void send();
          }}
        >
          {copy.citeSend}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
